import { VariantConfirmationMethod, VariantConfirmationVendor } from './methodVendorOptions';
import type { VariantConfirmation } from './types';

/**
 * A variant confirmation belongs to one of two workflows that share the same
 * data model but diverge in how confirmation moves across the states.
 *
 * - External: the variant is reconfirmed by a third-party vendor (Ambry,
 *   Baylor, Prevention, etc.). The "Request" action queues the confirmation
 *   in clinical and ships the sample to that vendor via LIMS.
 * - Internal: the variant is reconfirmed in-house at Color.
 *   It is never sent to an external vendor and uses its own set of actions.
 *
 * "Internal" is identified by the combination `vendor === Color` and
 * `method === NGS`, which is what the backend writes on auto-created internal
 * confirmations (see `monoweb.django_apps.assays.interfaces.variant_confirmation`).
 * Any other combination is treated as external, matching the backend's
 * `external_confirmations = qs.exclude(vendor=Color)` request handling.
 */
export enum ConfirmationWorkflow {
  EXTERNAL = 'external',
  INTERNAL = 'internal',
}

type WorkflowInputs = Pick<VariantConfirmation, 'method' | 'vendor'>;

export function getConfirmationWorkflow(confirmation: WorkflowInputs): ConfirmationWorkflow {
  const isColorNgs =
    confirmation.vendor === VariantConfirmationVendor.Color &&
    confirmation.method === VariantConfirmationMethod.NGS;
  return isColorNgs ? ConfirmationWorkflow.INTERNAL : ConfirmationWorkflow.EXTERNAL;
}

export function isInternalConfirmation(confirmation: WorkflowInputs): boolean {
  return getConfirmationWorkflow(confirmation) === ConfirmationWorkflow.INTERNAL;
}

export function isExternalConfirmation(confirmation: WorkflowInputs): boolean {
  return getConfirmationWorkflow(confirmation) === ConfirmationWorkflow.EXTERNAL;
}
